import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../booking/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Field } from "./components/crud";
import { IntegrationsPanel } from "./components/IntegrationsPanel";
import { ListInput, DateField } from "./components/FormFields";
import { ExportButton } from "./components/ExportButton";

export default function SettingsPage() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/api/admin/settings").then((r) => setS(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (!s) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const set = (k, v) => setS((d) => ({ ...d, [k]: v }));

  const saveAll = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/api/admin/settings", s);
      setS(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-settings-page" className="max-w-2xl">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Settings</h1>
        <div className="flex gap-2">
          <ExportButton />
          <Button data-testid="save-settings-btn" onClick={saveAll} disabled={saving}>{saving ? "Saving…" : "Save settings"}</Button>
        </div>
      </div>
      <div className="bg-white border rounded-lg p-6 space-y-4">
        <Field label="Business name"><Input data-testid="settings-business-name" value={s.business_name} onChange={(e) => set("business_name", e.target.value)} /></Field>
        <Field label="Staff alert emails (comma-separated) — new bookings, cancellations, waiting list, deposits, 09:00 recap">
          <ListInput testid="settings-staff-emails" value={s.staff_emails} onChange={(v) => set("staff_emails", v)} />
        </Field>
        <Field label="Page title"><Input value={s.page_title} onChange={(e) => set("page_title", e.target.value)} /></Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Regulars early access — from"><DateField testid="settings-priority-start" value={s.priority_start} onChange={(v) => set("priority_start", v)} /></Field>
          <Field label="Regulars early access — until"><DateField testid="settings-priority-end" value={s.priority_end} onChange={(v) => set("priority_end", v)} /></Field>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <Field label="Timezone"><Input value={s.timezone} onChange={(e) => set("timezone", e.target.value)} /></Field>
          <Field label="Currency"><Input value={s.currency} onChange={(e) => set("currency", e.target.value)} /></Field>
          <Field label="Default country code"><Input value={s.default_country_code} onChange={(e) => set("default_country_code", e.target.value)} /></Field>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <Field label="Earliest booking (min before)"><Input type="number" value={s.min_ahead_minutes} onChange={(e) => set("min_ahead_minutes", Number(e.target.value))} /></Field>
          <Field label="Latest booking (days ahead)"><Input type="number" value={s.max_ahead_days} onChange={(e) => set("max_ahead_days", Number(e.target.value))} /></Field>
          <Field label="Start interval default (min)"><Input type="number" value={s.default_interval_minutes} onChange={(e) => set("default_interval_minutes", Number(e.target.value))} /></Field>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <Field label="No-show cutoff (hours)"><Input type="number" value={s.no_show_cutoff_hours} onChange={(e) => set("no_show_cutoff_hours", Number(e.target.value))} /></Field>
          <Field label="Auto-release time"><Input value={s.auto_release_time} onChange={(e) => set("auto_release_time", e.target.value)} /></Field>
          <Field label="Auto-release on"><Switch checked={s.auto_release_enabled} onCheckedChange={(v) => set("auto_release_enabled", v)} /></Field>
          <Field label="Midday massage WhatsApp offer">
            <Switch data-testid="settings-massage-offer" checked={s.massage_offer_enabled !== false} onCheckedChange={(v) => set("massage_offer_enabled", v)} />
          </Field>
          <Field label="Online payments (Stripe deposits)">
            <Switch data-testid="settings-payments-enabled" checked={!!s.payments_enabled} onCheckedChange={(v) => set("payments_enabled", v)} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Business phone"><Input value={s.business_phone} onChange={(e) => set("business_phone", e.target.value)} /></Field>
        </div>
        <Field label="GTM ID (optional)"><Input value={s.gtm_id} onChange={(e) => set("gtm_id", e.target.value)} placeholder="GTM-XXXXXXX" /></Field>
        <Field label="Booking page languages (comma-separated codes)">
          <ListInput testid="settings-languages" value={s.languages} onChange={(v) => set("languages", v)} />
        </Field>
      </div>
      <IntegrationsPanel />
    </div>
  );
}
